import { useUpdateEffect } from 'ahooks';
import uniqueId from 'lodash-es/uniqueId';
import React, { useState } from 'react';
import {
  customEvents,
  getLabel,
  persistentStorage,
  TApiaFilter,
} from '@apia/util';
import { shallowEqual } from '@apia/store';
import { useOtherTagButton } from '@apia/components';
import { Icon } from '@apia/icons';
import { HeaderCell } from './HeaderCell';
import { useResponsiveTableContext } from './ResponsiveTableContext';
import { useResponsiveTable } from './store';
import { StateCell } from './StateCell';
import { FilterCell } from './FilterCell';
import { useDomProps } from './store/selection/useDomProps';
import { domPropsSelector } from './ResponsiveTable';

declare global {
  interface Window {
    focusedRow: number;
    focusedColumn: number;
    hasNonAdditionalFilters: boolean;
  }
}

const NoMemoHeader = () => {
  const { name, FiltersRenderer } = useResponsiveTableContext();

  const columns = useResponsiveTable(
    (global) => global.responsiveTableSlice[name]?.columns,
  );
  const hasNonAdditionalFilters = useResponsiveTable(
    (global) => global.responsiveTableSlice[name]?.hasNonAdditionalFilters,
  );
  const focusedColumn = useResponsiveTable(
    (global) => global.responsiveTableSlice[name]?.focusedColumn,
  );
  const focusedRow = useResponsiveTable(
    (global) => global.responsiveTableSlice[name]?.focusedRow,
  );

  const filters = useResponsiveTable<
    {
      columnIndex: number;
      definition: TApiaFilter;
    }[]
  >(
    (global) => {
      const tableState = global.responsiveTableSlice[name];
      const returnObject = tableState?.filters
        ?.map((current) => ({
          columnIndex: tableState.columns.findIndex(
            (search) => search.name === current.column,
          ),
          definition: current,
        }))
        .filter((current) => current.columnIndex >= 0);

      return returnObject;
    },
    (a, b) => {
      return (
        a?.length === b?.length &&
        a?.reduce<boolean>((result, current, index) => {
          return (
            result && shallowEqual(current.definition, b[index].definition)
          );
        }, true)
      );
    },
  );

  const statesCount = useResponsiveTable((globalState) => {
    const state = globalState.responsiveTableSlice[name];
    return state?.statesColumns ?? 0;
  }, shallowEqual);

  const additionalColumns = React.useMemo(
    () =>
      columns?.reduce(
        (count, column) => count + (column.showAsAdditional ? 1 : 0),
        0,
      ),
    [columns],
  );

  const statesIds = React.useMemo(() => {
    return Array(statesCount)
      .fill('')
      .map(() => uniqueId());
  }, [statesCount]);

  const [isFiltersRowVisible, setIsFiltersRowVisible] = React.useState<boolean>(
    /* (persistentStorage.showResponsiveTableFilters as boolean) ??  */ false,
  );
  React.useEffect(() => {
    persistentStorage.showResponsiveTableFilters = isFiltersRowVisible;
  }, [isFiltersRowVisible]);

  const buttonProps = useOtherTagButton(
    React.useMemo(
      () => ({
        className: 'additionalColumn',
        onClick: () => setIsFiltersRowVisible((current) => !current),
      }),
      [],
    ),
  );

  const [ref, setRef] = useState<HTMLTableRowElement | null>(null);

  useUpdateEffect(() => {
    const handleFocus = (ev: Event) => {
      ev.stopPropagation();
      setIsFiltersRowVisible(true);
    };
    ref?.addEventListener(customEvents.focus, handleFocus);
    return () => {
      ref?.removeEventListener(customEvents.focus, handleFocus);
    };
  }, [ref]);

  const domProps = useDomProps('headerTr', {}, domPropsSelector(name));

  return (
    <thead>
      <tr {...domProps}>
        {(additionalColumns > 0 || hasNonAdditionalFilters) && (
          <th
            aria-colindex={1}
            aria-rowindex={1}
            data-focused={
              focusedColumn === 1 &&
              focusedRow === -1 - (hasNonAdditionalFilters ? 1 : 0)
            }
            {...buttonProps}
            tabIndex={
              focusedColumn === 1 &&
              focusedRow === -1 - (hasNonAdditionalFilters ? 1 : 0)
                ? 0
                : -1
            }
            aria-label={
              isFiltersRowVisible
                ? getLabel('lblHideFilters').text
                : getLabel('lblShowFilters').text
            }
            role="rowheader"
          >
            {hasNonAdditionalFilters ? (
              <Icon
                className="filterButton"
                name="Filter"
                title={
                  isFiltersRowVisible
                    ? getLabel('lblHideFilters').text
                    : getLabel('lblShowFilters').text
                }
                aria-label={
                  isFiltersRowVisible
                    ? getLabel('lblHideFilters').text
                    : getLabel('lblShowFilters').text
                }
              />
            ) : (
              <>
                <span style={{ display: 'none' }}>
                  {isFiltersRowVisible
                    ? getLabel('lblHideFilters').text
                    : getLabel('lblShowFilters').text}
                </span>
                <span>&nbsp;</span>
              </>
            )}
          </th>
        )}
        {statesIds.map((id, i) => (
          <StateCell isHeader key={id} index={i} rowIndex={1} />
        ))}
        {columns
          ?.map((current, actualColumnIndex) => ({
            ...current,
            actualTableColumnIndex: actualColumnIndex,
          }))
          .filter((current) => !current.showAsAdditional)
          .map((current, columnIndex) => {
            if (current.hidden) return null;

            return (
              <HeaderCell
                apiaColumnIndex={current.actualTableColumnIndex}
                columnIndex={current.actualTableColumnIndex}
                inTableColumnIndex={columnIndex}
                key={current.name}
              />
            );
          })}
      </tr>
      {(filters?.length ?? 0) > 0 && (
        <tr
          aria-rowindex={2}
          data-rowindex={-1}
          ref={setRef}
          data-focusindex={2}
          className={`responsiveTable__filters__row ${
            isFiltersRowVisible ? '' : 'hidden'
          }`}
        >
          {(additionalColumns > 0 || hasNonAdditionalFilters) && (
            <td
              className="noFilter"
              aria-colindex={1}
              aria-rowindex={2}
              tabIndex={focusedColumn === 1 && focusedRow === -1 ? 0 : -1}
              data-focused={focusedColumn === 1 && focusedRow === -1}
            >
              &nbsp;
            </td>
          )}
          {statesIds.map((id, i) => (
            <StateCell isHeader key={id} index={i} rowIndex={2} />
          ))}
          {columns
            ?.filter((current) => !current.showAsAdditional && !current.hidden)
            .map((current, columnIndex) => {
              const filter = filters.find(
                (search) => search.definition.column === current.name,
              );

              const RenderComponent = filter
                ? FiltersRenderer?.(String(filter.definition.id))
                : undefined;

              return current.showAsAdditional ? null : (
                <FilterCell
                  key={current.name}
                  columnIndex={columnIndex}
                  filter={filter?.definition}
                  Renderer={RenderComponent}
                />
              );
            })}
        </tr>
      )}
    </thead>
  );
};

export const Header = React.memo(NoMemoHeader);
